import axios from 'axios'
import config from 'config'
import * as jose from 'jose'
import { v4 as uuid } from 'uuid'

import { readableError } from 'utils/error'

import { R_SIGNON, VALIDATION_KEY, authDebug, initialState } from './defs'
import { safeStoreDrop, safeStoreGet, safeStorePut } from './localStore'

export function signonInitialState() {
  const hasValKey = !!validationKeyRetrieve()
  authDebug(5, '[auth/lib] signonInitialState()', hasValKey)
  return {
    ...initialState,
    refreshToken: hasValKey ? 1 : 0
  }
}

////////////////////////////////////////////////////////////////////////////////
// mutable global -- not ideal, but we have a tough challengeInput
const defaultAccessToken = { token: '', expires: 0, valid: false, claims: {} }
let ACCESS_TOKEN = { ...defaultAccessToken }

export function isCurrentAccessTokenExpired() {
  return Date.now() > ACCESS_TOKEN.expires
}

export function currentAccessTokenRetrieve(_dispatch) {
  if (ACCESS_TOKEN.valid && isCurrentAccessTokenExpired()) {
    authDebug(3, '[auth/lib] currentAccessTokenRetrieve()', `expired - removing`)
    currentAccessTokenSet(null)
  }
  return ACCESS_TOKEN
}

export function currentAccessTokenSet(token) {
  ACCESS_TOKEN = processAccessToken(token)
  authDebug(3, '[auth/lib] currentAccessTokenSet()')
}

export function currentAccessTokenReset() {
  authDebug(5, '[auth/lib] currentAccessTokenReset()', '')
  ACCESS_TOKEN = { ...defaultAccessToken }
}

function processAccessToken(token) {
  authDebug(5, '[auth/lib] processAccessToken()', token)
  if (!token) {
    return { ...defaultAccessToken }
  }

  const claims = jose.decodeJwt(token)
  authDebug(4, '[auth/lib] token claims', [claims, token])
  if (Date.now() / 1000 < claims.exp) {
    return { claims, token, expires: claims.exp * 1000, valid: true }
  }
  return { ...defaultAccessToken }
}

////////////////////////////////////////////////////////////////////////////////
function validationKeyRetrieve() {
  const key = safeStoreGet(VALIDATION_KEY)
  authDebug(3, '[auth/lib] validationKeyRetrieve() key exists=', !!key)
  return key
}

function validationKeySave(key) {
  authDebug(3, '[auth/lib] validationKeySave()', key)
  safeStorePut(VALIDATION_KEY, key)
}

function validationKeyDrop() {
  authDebug(3, '[auth/lib] validationKeyDrop()', '')
  safeStoreDrop(VALIDATION_KEY)
}

////////////////////////////////////////////////////////////////////////////////
// Try to get/refresh token and then signInUser or SIGNOUT
export async function getAccessToken(dispatch) {
  // authDebug(3, '[auth/lib] getAccessToken()', '')
  // BJG: not sure if this is needed
  // dispatch({ type: R_SIGNON.MERGE, value: { refresh: 0 } }) // prevents further calling in app.jsx
  const { valid } = currentAccessTokenRetrieve(dispatch)
  if (!valid) {
    authDebug(
      1,
      '[auth/lib] getAccessToken()',
      'invalid current token, starting refresh'
    )
    const validation_key = validationKeyRetrieve()
    if (validation_key) {
      const newtok = await generateRefreshToken()
      if (newtok) {
        return authRequest(
          'refresh',
          {
            body: JSON.stringify({
              client_assertion_type:
                'urn:ietf:params:oauth:client-assertion-type:jwt-bearer',
              client_assertion: newtok
            })
          },
          dispatch
        )
          .then((result) => {
            authDebug(2, '[auth/lib] getAccessToken():', result)
            if (result && result.access_token) {
              const jwt = jose.decodeJwt(result.access_token)
              if (jwt.sub.substring(0, 5) === 'cas1:') {
                currentAccessTokenSet(result.access_token)
                const sub = jwt.sub.substring(5)
                dispatch({ type: R_SIGNON.SIGNED_IN, sub })
              }
            } else {
              dispatch({ type: R_SIGNON.SIGN_OUT })
            }
            return result
          })
          .catch((err) => {
            console.error('UNHANDLED ERR', err)
          })
      } else {
        authDebug(5, '[auth/lib] getAccessToken():', 'no refresh token?')
      }
    }
  }
  // we don't have a valid access token, but that could just be because the
  // server is offline. for now just drop access token state data
  currentAccessTokenReset()
  authDebug(3, '[auth/lib] getAccessToken()', 'no access token')
}

async function generateRefreshToken() {
  authDebug(5, '[auth/lib] generateRefreshToken()')
  const validation_key = safeStoreGet(VALIDATION_KEY)
  if (validation_key) {
    const { secret, subject, audience } = validation_key
    const key = new TextEncoder().encode(secret)
    const jwt = await new jose.SignJWT({ jti: uuid() })
      .setProtectedHeader({ alg: 'HS256' })
      .setIssuedAt()
      .setAudience(audience)
      .setExpirationTime('15m')
      .setSubject(subject)
      .sign(key)
    return jwt
  } else {
    return false
  }
}

////////////////////////////////////////////////////////////////////////////////
// handlers from the various signOn interfaces
export function startSignon({ vars, dispatch }) {
  signonDropStates()
  authDebug(1, '[auth/lib] startSignon()', 'querying for new token')
  return authRequest(
    'signon',
    {
      body: JSON.stringify(vars)
    },
    dispatch
  )
    .then((data) => handleValidate({ data, dispatch }))
    .catch((error) => authError({ dispatch, msg: readableError(error) }))
}

async function handleValidate({ data, dispatch }) {
  authDebug(2, '[auth/lib] handleValidate() data=', data)
  if (data.aud && data.sec && data.sub) {
    let token = {
      audience: data.aud,
      secret: data.sec,
      subject: data.sub
    }
    validationKeySave(token)
    return getAccessToken(dispatch)
  } else if (data.reason) {
    authError({ dispatch, msg: readableError(data.reason) })
  } else {
    authError({
      dispatch,
      msg: 'response received from backend with no generateRefreshToken token? cannot continue'
    })
  }
}

////////////////////////////////////////////////////////////////////////////////
export function signonDropStates() {
  authDebug(1, '[auth/lib] signonDropStates()')
  currentAccessTokenReset()
  validationKeyDrop()
}

export function authRequest(path, opts, dispatch) {
  authDebug(3, '[auth/lib] authRequest():', `{api}/${path}`)
  if (!opts.headers) {
    opts.headers = {}
  }
  if (!opts.headers['Content-Type']) {
    opts.headers['Content-Type'] = 'application/json'
  }
  return axios
    .post(`${config.app}${config.authapi}${path}`, opts.body, {
      headers: opts.headers,
      withCredentials: true
    })
    .then((res) => res.data)
    .catch((e) => {
      if (dispatch) {
        switch (e.message) {
          case 'Request failed with status code 403':
            dispatch({
              type: R_SIGNON.ERROR_REDIRECT,
              value: 'Cached signin failed, please signin again.'
            })
            break
          default:
            authError({ dispatch, msg: `${e}` })
            break
        }
      }
      return Promise.reject(e.message)
    })
}

export function authError({ dispatch, msg }) {
  authDebug(3, '[auth/lib] authError() msg=', msg)
  dispatch({ type: R_SIGNON.ERROR, value: msg })
}
